import { ChevronDownIcon } from '@heroicons/react/16/solid';
import React from 'react';
import { ResolutionString, SeriesType } from '../../../charting_library';
import { cn } from '../../../lib/classNameHelpers';
import { useArchiveDb } from '../../../lib/config';
import { type ChartPriceType } from '../../../lib/tradingViewHelpers';
import { Button } from '../../catalyst-ui-kit/button';
import { FUNDING_CHART_TIMESCALES, FundingTimescale } from '../../FundingHistory/timescales';
import { InlineSelect } from '../../InlineSelect/InlineSelect';
import { ScrollContainer } from '../../ScrollContainer/ScrollContainer';
import { ChartDisplaySettings } from './ChartDisplaySettings';
import { ChartPriceSettings } from './ChartPriceSettings';
import { ChartResolutionSelector } from './ChartResolutionSelector';
import { ChartTypeSelector } from './ChartTypeSelector';

export interface TradingViewHeaderProps {
  chartResolution: ResolutionString;
  chartType: SeriesType;
  priceType: ChartPriceType;
  setChartResolution: (resolution: ResolutionString) => void;
  setChartType: (chartType: SeriesType) => void;
  setPriceType: (priceType: ChartPriceType) => void;
  onIndicatorsClick: () => void;
  className?: string;
  chartTab?: 'price' | 'funding' | 'depth' | 'details' | 'orderbook';
  fundingTimescale?: FundingTimescale;
  setFundingTimescale?: (timescale: FundingTimescale) => void;
}

export const TradeChartsHeader: React.FC<TradingViewHeaderProps> = ({
  chartType,
  chartResolution,
  priceType,
  setChartResolution,
  setChartType,
  setPriceType,
  onIndicatorsClick,
  className,
  chartTab,
  fundingTimescale,
  setFundingTimescale,
}) => {
  return (
    <ScrollContainer
      direction="horizontal"
      className={cn('flex flex-1 items-center py-2 pl-1 ml-1 pr-1', className)}
      fadeSize={30}
    >
      {/* Trading View options */}
      {chartTab === 'price' && (
        <>
          {/* Resolution Selector */}
          <ChartResolutionSelector value={chartResolution} onChange={setChartResolution} />

          <div className="mx-1 h-full w-px shrink-0 bg-contentTertiary opacity-25"></div>

          {/* Chart Type Selector */}
          <ChartTypeSelector value={chartType} onChange={setChartType} />

          {/* Vertical divider */}
          <div className="mx-1 h-full w-px shrink-0 bg-contentTertiary opacity-25" />

          <Button
            text
            size="xs"
            onClick={onIndicatorsClick}
            className="group flex items-center gap-1 whitespace-nowrap !p-1 !text-contentSecondary hover:!text-contentPrimary"
            title="Toggle indicators modal"
          >
            Indicators
            <ChevronDownIcon
              className="size-3 shrink-0 fill-contentSecondary transition-all group-hover:fill-contentPrimary"
              aria-hidden="true"
            />
          </Button>

          <ChartDisplaySettings />

          {useArchiveDb && <ChartPriceSettings priceType={priceType} setPriceType={setPriceType} />}
        </>
      )}

      {/* Funding history options */}
      {chartTab === 'funding' && fundingTimescale && setFundingTimescale && (
        <InlineSelect
          selected={fundingTimescale}
          options={FUNDING_CHART_TIMESCALES}
          onChange={(option) => option && setFundingTimescale(option.value)}
          className="gap-1"
          buttonClassName="!p-1"
        />
      )}
    </ScrollContainer>
  );
};
